import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table, Input,
  Button,
  Modal,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import IntlMessages from 'util/IntlMessages';
import InfoView from 'components/common/InfoView';
import RoleForm from './RoleForm';
import {
  getRoles,
  getPermissions,
  addRole,
  updateRole,
  deleteRole,
} from '../../redux/actions/Auth';
import { roleColumns } from '../../components/common/Columns';
import Can from '../../util/can';
import { ROLES } from '../../constants/resources';
import generateQuery, { handleSearch as search } from '../../util/table';
import useForm from '../../hooks/useForm';

const Roles = () => {
  const dispatch = useDispatch();
  const {
    data: permissions, loading: loadingPermissions,
  } = useSelector(({ auth }) => auth.permissions);
  const {
    data: roles,
    loading: loadingRoles,
    addingRole,
    totalItems,
    pageSize,
    currentPage: current,
    updatingRole,
  } = useSelector(({ auth }) => auth.roles);

  useEffect(() => {
    const pagination = `page=${current}&limit=${pageSize}`;
    dispatch(getRoles(pagination));
    dispatch(getPermissions('limit=1000'));
  }, [dispatch]);

  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [searchParams, setSearchParams] = useState('');
  const [selectedType, setselectedType] = useState();
  const {
    form,
    onFormOk,
    formVisibility,
    handleShowModal,
    handleCancel,
    handleEditRecord,
    editableRecord,
  } = useForm();

  const permissionById = permissions.length && permissions.reduce(
    (acc, perm) => ({ ...acc, [perm._id]: perm }), {},
  );
  const permissionByName = permissions.length && permissions.reduce(
    (acc, perm) => ({ ...acc, [perm.name]: perm }), {},
  );
  const dataSource = permissions.length
    && Object.keys(permissionById).length > 0 && roles.length && roles.map((role) => ({
      key: role._id,
      name: role.name,
      is_system_role: role.is_system_role,
      permissions: role.permissions && role.permissions.length
        ? role.permissions.map((permissionId) => permissionById[permissionId]?.name)
        : []
    }));
  const editable = Object.keys(editableRecord).length !== 0;

  const handleDeleteRole = (id) => {
    dispatch(deleteRole(id));
  };

  const handleEditRole = (record) => {
    setSelectedPermissions(
      record.permissions.map((permission) => permissionByName[permission]?._id),
    );
    if (record.is_system_role) {
      setselectedType('is_system_role');
    } else if (record.is_system_role === false) {
      setselectedType('is_merchant');
    }
  };

  const handleOk = (values) => {
    const roleData = {
      ...values,
      permissions: selectedPermissions,
      is_system_role: selectedType === 'is_system_role' ? true : false
    };
    if (editable) {
      dispatch(updateRole(editableRecord.key, roleData));
    } else {
      dispatch(addRole(roleData));
    }
  };

  const handleOnChange = (pagination, filters, sorter) => {
    dispatch(getRoles(generateQuery(pagination, filters, sorter, searchParams)));
  };

  const handleSearch = (searchValue) => {
    const query = search(
      searchValue.trim().replace(/([^a-zA-Z0-9])/g, '\\$1'),
      dispatch, getRoles, roleColumns,
    );
    setSearchParams(query);
  };

  const props = {
    loading: (loadingPermissions || loadingRoles),
    pagination: {
      current,
      pageSize,
      total: totalItems,
      showSizeChanger: false,
    },
    columns: roleColumns(handleDeleteRole, handleEditRole, handleEditRecord),
    dataSource,
    onChange: handleOnChange,
  };

  return (
    (
      <div>
        <h2 className="title gx-mb-4"><IntlMessages id="roles.title" /></h2>
        <Can I="create" a={ROLES}>
          <Button
            icon={<PlusOutlined />}
            type="primary"
            onClick={() => {
              setselectedType();
              handleShowModal();
            }}
            data-testid="add-role-btn"
          >
            Add Role
          </Button>
        </Can>
        <div className="justify-content-center">
          <Input.Search
            data-testid="search-input"
            placeholder="Search roles"
            enterButton
            allowClear
            onSearch={handleSearch}
          />
        </div>
        <div className="justify-content-center">
          <Table data-testid="roles-table" className="gx-table-responsive" {...props} />
        </div>
        <Modal
          title={editable ? 'Edit Role' : 'Add Role'}
          open={formVisibility}
          okText={editable ? 'Save' : 'Add'}
          onOk={() => onFormOk(handleOk)}
          confirmLoading={addingRole || updatingRole}
          onCancel={() => {
            handleCancel();
            setSelectedPermissions([]);
          }}
        >
          <RoleForm {...{
            form, selectedPermissions, setSelectedPermissions, setselectedType, selectedType,
          }}
          />
        </Modal>
        <InfoView />
      </div>
    )
  );
};

export default Roles;
